/**
 * Componente Visual: Modal de Exercícios
 *
 * Responsabilidade:
 * - Apresentar a interface do usuário (UI) para criação e edição de exercícios.
 * - Receber dados e callbacks via props.
 * - Utilizar o hook useExerciseModal para lógica e estado.
 * - Não conter lógica de negócios direta.
 */

import {
  FiX,
  FiImage,
  FiVideo,
  FiEdit3,
  FiFileText,
  FiInfo,
  FiUpload,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiTrash2,
} from "react-icons/fi";
import AuthenticatedImage from "../ui/AuthenticatedImage";
import { ExerciseModalLogicProps } from "./exerciseModalTypes";
import { useExerciseModal } from "./useExerciseModal";

function ExercicieModal(props: ExerciseModalLogicProps) {
  const {
    form,
    imagePreview,
    videoPreview,
    loading, // Used in submit, but not in UI? Ah, usually loading is used for button state but I don't see it in the original JSX. Let me check the original JSX again.
    videoSectionExpanded,
    imageSectionExpanded,
    isReadOnly,
    submit,
    handleImageChange,
    handleVideoChange,
    clearImage,
    toggleVideoSection,
    toggleImageSection,
    updateField,
  } = useExerciseModal(props);

  const { isOpen, onBack } = props;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-full h-[100dvh] sm:h-auto sm:max-w-4xl sm:max-h-[95vh] border-0 sm:border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark flex flex-col sm:rounded-2xl overflow-hidden">
        {/* Header Compacto Mobile */}
        <div className="relative border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-4 py-3 sm:px-6 sm:py-4 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30 flex-shrink-0">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

          <button
            onClick={onBack}
            className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 rounded-xl p-2 text-gray-500 outline-none transition-all hover:bg-white hover:text-gray-700 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-boxdark dark:hover:text-gray-200 touch-manipulation active:scale-95"
            aria-label="Fechar modal"
          >
            <FiX size={20} className="sm:w-6 sm:h-6" />
          </button>

          <div className="relative flex items-center gap-3 pr-12 sm:pr-0">
            <div className="rounded-xl bg-gradient-to-br from-purple-600 to-purple-600 p-2 text-white shadow-sm flex-shrink-0">
              {form.id ? (
                <FiEdit3 size={18} className="sm:w-6 sm:h-6" />
              ) : (
                <FiImage size={18} className="sm:w-6 sm:h-6" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">
                {form.id ? "Editar Exercício" : "Novo Exercício"}
              </h3>
              <p className="mt-0.5 text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-tight line-clamp-1">
                {form.id
                  ? "Atualize as informações"
                  : "Preencha os dados básicos"}
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <form
          id="exercise-form"
          onSubmit={submit}
          className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-6 -webkit-overflow-scrolling-touch"
        >
          <div className="space-y-4 pb-4 sm:pb-0">
            {/* Alerta de Acesso Restrito */}
            {isReadOnly && (
              <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-amber-50 p-3 dark:border-amber-700 dark:from-amber-900/30 dark:to-amber-900/30">
                <div className="flex items-start gap-2.5">
                  <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40 flex-shrink-0">
                    <FiInfo
                      className="text-amber-600 dark:text-amber-400"
                      size={16}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h5 className="mb-1 font-bold text-sm text-amber-900 dark:text-amber-300">
                      Acesso Restrito
                    </h5>
                    <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-400">
                      Este exercício é público e não pode ser alterado.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Seção: Informações Básicas */}
            <div className="rounded-xl border border-stroke bg-white p-4 shadow-sm dark:border-strokedark dark:bg-boxdark">
              <div className="mb-4 flex items-center gap-2">
                <div className="h-1 w-8 rounded bg-purple-600"></div>
                <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  Informações Básicas
                </h4>
              </div>

              <div className="space-y-4">
                {/* Nome do Exercício */}
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Nome do Exercício <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiEdit3 size={16} />
                    </span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      disabled={isReadOnly}
                      value={form.name || ""}
                      onChange={({ target }) =>
                        updateField("name", target.value)
                      }
                      placeholder="Ex: Supino Reto"
                      className="w-full rounded-xl border border-stroke bg-gray-50 py-3 pl-11 pr-4 text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Descrição */}
                <div>
                  <label
                    htmlFor="description"
                    className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Descrição (opcional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-3.5 text-gray-400 pointer-events-none z-10">
                      <FiFileText size={16} />
                    </span>
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      disabled={isReadOnly}
                      value={form.description || ""}
                      onChange={({ target }) =>
                        updateField("description", target.value)
                      }
                      placeholder="Músculos trabalhados, dicas..."
                      className="w-full rounded-xl border border-stroke bg-gray-50 py-3 pl-11 pr-4 text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation resize-none disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Seção: Imagem */}
            <div className="rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden">
              <button
                type="button"
                onClick={toggleImageSection}
                className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors active:bg-gray-100 dark:active:bg-gray-900/50 touch-manipulation"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="h-1 w-8 rounded bg-purple-600 flex-shrink-0"></div>
                  <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 truncate">
                    Imagem do Exercício
                  </h4>
                  {form.image && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300 flex-shrink-0">
                      <FiCheck size={12} />
                      <span className="hidden sm:inline">Adicionada</span>
                    </span>
                  )}
                </div>
                {imageSectionExpanded ? (
                  <FiChevronUp size={20} className="text-gray-500 shrink-0" />
                ) : (
                  <FiChevronDown size={20} className="text-gray-500 shrink-0" />
                )}
              </button>

              {imageSectionExpanded && (
                <div className="px-4 pb-4 pt-0">
                  {/* Preview da Imagem */}
                  <div className="mb-3 relative flex h-40 sm:h-56 w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-stroke bg-gradient-to-br from-gray-50 to-gray-100 dark:border-strokedark dark:from-gray-800 dark:to-gray-900">
                    {form.image ? (
                      <AuthenticatedImage
                        fileName={imagePreview}
                        alt="Preview do exercício"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          if (isReadOnly) {
                            e.preventDefault();
                            return;
                          }
                          const input = document.getElementById(
                            "exercise-image-input",
                          ) as HTMLInputElement;
                          if (input) {
                            input.click();
                          }
                        }}
                        disabled={isReadOnly}
                        className="flex flex-col items-center gap-2 p-4 w-full h-full justify-center group focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation active:scale-95"
                        aria-label="Adicionar imagem do exercício"
                      >
                        <div className="rounded-xl bg-gray-200 p-3 dark:bg-gray-700 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/40 transition-colors">
                          <FiImage
                            size={28}
                            className="text-gray-400 group-hover:text-purple-500 transition-colors"
                          />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 group-hover:text-purple-500 transition-colors">
                          Nenhuma imagem
                        </p>
                        <p className="text-xs text-gray-400 text-center group-hover:text-purple-400 transition-colors">
                          Toque para adicionar
                        </p>
                      </button>
                    )}
                  </div>

                  {/* Botões: Remover e Upload/Trocar */}
                  <div className="space-y-2">
                    {form.image && !isReadOnly && (
                      <button
                        type="button"
                        onClick={clearImage}
                        className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-3 text-base font-semibold text-red-600 transition-all hover:border-red-300 hover:bg-red-100 active:scale-98 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400 dark:hover:border-red-800 dark:hover:bg-red-900/30 touch-manipulation"
                        aria-label="Remover imagem"
                      >
                        <FiTrash2 size={18} />
                        Remover Imagem
                      </button>
                    )}

                    <label
                      className={`group relative flex w-full items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-stroke bg-gray-50 px-4 py-3.5 text-base font-semibold text-gray-700 transition-all hover:border-purple-500 hover:bg-purple-50 hover:text-purple-600 active:scale-98 dark:border-strokedark dark:bg-boxdark/50 dark:text-gray-300 dark:hover:border-purple-500 dark:hover:bg-purple-950/20 dark:hover:text-purple-400 touch-manipulation ${
                        isReadOnly
                          ? "cursor-not-allowed opacity-60"
                          : "cursor-pointer"
                      }`}
                    >
                      <FiUpload
                        size={18}
                        className="transition-transform group-hover:scale-110"
                      />
                      {form.image ? "Trocar Imagem" : "Selecionar Imagem"}
                      <input
                        id="exercise-image-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={isReadOnly}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                    JPG, PNG, GIF • Máx. 5MB
                  </p>
                </div>
              )}
            </div>

            {/* Seção: Vídeo */}
            <div className="rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden">
              <button
                type="button"
                onClick={toggleVideoSection}
                className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors active:bg-gray-100 dark:active:bg-gray-900/50 touch-manipulation"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="h-1 w-8 rounded bg-purple-600 flex-shrink-0"></div>
                  <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 truncate">
                    Vídeo (opcional)
                  </h4>
                  {(videoPreview || form.video) && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300 flex-shrink-0">
                      <FiCheck size={12} />
                      <span className="hidden sm:inline">Adicionado</span>
                    </span>
                  )}
                </div>
                {videoSectionExpanded ? (
                  <FiChevronUp size={20} className="text-gray-500 shrink-0" />
                ) : (
                  <FiChevronDown size={20} className="text-gray-500 shrink-0" />
                )}
              </button>

              {videoSectionExpanded && (
                <div className="px-4 pb-4 pt-0">
                  {/* Preview do Vídeo */}
                  <div className="mb-3 relative flex h-40 sm:h-56 w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-stroke bg-gradient-to-br from-gray-50 to-gray-100 dark:border-strokedark dark:from-gray-800 dark:to-gray-900">
                    {videoPreview || form.video ? (
                      <video
                        src={videoPreview || form.video}
                        controls
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-contain"
                        style={{ maxHeight: "100%" }}
                      >
                        <track kind="captions" src="" label="Nenhuma legenda" />
                        Seu navegador não suporta a reprodução de vídeos.
                      </video>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          if (isReadOnly) {
                            e.preventDefault();
                            return;
                          }
                          const input = document.getElementById(
                            "exercise-video-input",
                          ) as HTMLInputElement;
                          if (input) {
                            input.click();
                          }
                        }}
                        disabled={isReadOnly}
                        className="flex flex-col items-center gap-2 p-4 w-full h-full justify-center group focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation active:scale-95"
                        aria-label="Adicionar vídeo do exercício"
                      >
                        <div className="rounded-xl bg-gray-200 p-3 dark:bg-gray-700 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/40 transition-colors">
                          <FiVideo
                            size={28}
                            className="text-gray-400 group-hover:text-purple-500 transition-colors"
                          />
                        </div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 group-hover:text-purple-500 transition-colors">
                          Nenhum vídeo
                        </p>
                        <p className="text-xs text-gray-400 text-center group-hover:text-purple-400 transition-colors">
                          Toque para adicionar
                        </p>
                      </button>
                    )}
                  </div>

                  {/* Botões: Remover e Upload/Trocar */}
                  <div className="space-y-2">
                    {form.video && !isReadOnly && (
                      <button
                        type="button"
                        onClick={() => {
                          updateField("video", "");
                        }}
                        className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-3 text-base font-semibold text-red-600 transition-all hover:border-red-300 hover:bg-red-100 active:scale-98 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400 dark:hover:border-red-800 dark:hover:bg-red-900/30 touch-manipulation"
                        aria-label="Remover vídeo"
                      >
                        <FiTrash2 size={18} />
                        Remover Vídeo
                      </button>
                    )}

                    <label
                      className={`group relative flex w-full items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-stroke bg-gray-50 px-4 py-3.5 text-base font-semibold text-gray-700 transition-all hover:border-purple-500 hover:bg-purple-50 hover:text-purple-600 active:scale-98 dark:border-strokedark dark:bg-boxdark/50 dark:text-gray-300 dark:hover:border-purple-500 dark:hover:bg-purple-950/20 dark:hover:text-purple-400 touch-manipulation ${
                        isReadOnly
                          ? "cursor-not-allowed opacity-60"
                          : "cursor-pointer"
                      }`}
                    >
                      <FiUpload
                        size={18}
                        className="transition-transform group-hover:scale-110"
                      />
                      {form.video ? "Trocar Vídeo" : "Selecionar Vídeo"}
                      <input
                        id="exercise-video-input"
                        type="file"
                        accept="video/*"
                        onChange={handleVideoChange}
                        disabled={isReadOnly}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                    MP4, WebM • Máx. 50MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Botão de Salvar */}
          {!isReadOnly && (
            <div className="mt-6 sm:mt-8 pb-safe sm:pb-0">
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-purple-600 py-3.5 text-base font-bold text-white shadow-lg shadow-purple-200 transition-all hover:bg-purple-700 hover:shadow-purple-300 active:scale-98 dark:shadow-purple-900/20 dark:hover:shadow-purple-900/30 disabled:opacity-70 disabled:cursor-not-allowed touch-manipulation"
              >
                {loading ? "Salvando..." : "Salvar Exercício"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default ExercicieModal;
